import { Maybe } from 'maybeasy';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../../../DashboardSummary/useClickOutside';
import CourseCatalog, { CatalogLayout } from './CourseCatalog';
import { CourseOption } from './CourseOptions';
import SelectedCourse, { RunColumn, runChooser } from './SelectedCourse';

interface Props {
  options: ReadonlyArray<CourseOption>;
  selected: Maybe<CourseOption>;
  runs: RunColumn;
  catalog: CatalogLayout;
  onPick: (option: CourseOption) => void;
}

// The course card and the catalog it opens, with nothing said about what a
// pick means: the invite page aims at a run and the Share overlay at a course
// family (DVLP-7976, DVLP-8060). Both callers decide; this only opens, closes
// and shows.
function CoursePicker({ options, selected, runs, catalog, onPick }: Props) {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const closeCatalog = useCallback(() => setCatalogOpen(false), []);
  const clickOutsideRefs = useMemo(() => [rootRef], []);
  useClickOutside(clickOutsideRefs, catalogOpen, closeCatalog);

  const pick = (option: CourseOption) => {
    onPick(option);
    setCatalogOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <SelectedCourse
        option={selected}
        runs={runs}
        catalogOpen={catalogOpen}
        onToggleCatalog={() => setCatalogOpen(!catalogOpen)}
      />
      {catalogOpen && (
        <CourseCatalog
          options={options}
          selectedKey={selected.map((option) => option.key)}
          layout={catalog}
          chooser={runChooser(runs)}
          onPick={pick}
          onClose={closeCatalog}
        />
      )}
    </div>
  );
}

export default CoursePicker;
